import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import { ProOnly } from '../label/ProOnly';
import { useOutsideClick } from '../hooks';
import { NekoSpinner } from '../misc/Spinner';

import { Icon } from '@iconify/react';
import checkboxBlankCircleOutline from '@iconify/icons-mdi/checkbox-blank-circle-outline';
import recordCircle from '@iconify/icons-mdi/record-circle';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import chevronUp from '@iconify/icons-mdi/chevron-up';
import { NekoIcon } from '../misc/Icon';
import { NekoInput } from './Input';

const Select = Styled.div`
  border-radius: 8px;
  position: relative;
  user-select: none;
  cursor: pointer;

  &.show-options {
    background-color: ${Theme.blue};
    border-radius: 8px 8px 0 0;
  }

  &[data-is-disabled=true] {
    cursor: none;
    pointer-events: none;

    .nui-select-option {
      pointer-events: none;
      background: ${Theme.gray};
      padding-right: 10px;
    }
  }
`;

const SelectedOption = Styled.div`
  align-items: center;
  background-color: ${Theme.white};
  border: 2px solid ${Theme.blue};
  border-radius: 8px;
  display: flex;
  font-family: ${Theme.fontFamily};
  font-size: 14px;
  padding: 0 5px 0 10px;
  box-sizing: border-box;
  height: 30px;

  &.isBusy {
    padding-left: 5px;
  }

  .rightContent {
    align-items: center;
    display: flex;
    margin-left: auto;
  }
`;

const Description = Styled.div`
  display: block;
  margin-top: 5px;
  font-size: ${Theme.fontSizeSmall};
  line-height: 14px;
  color: ${Theme.darkGray};

  * {
    font-size: ${Theme.fontSizeSmall};
    line-height: inherit;
    margin: 0;
  }
`;

const Options = Styled.div`
  border-radius: 8px;
  overflow: hidden;
  padding-top: 28px;
  position: absolute;
  top: 0;
  z-index: 10;
  width: 100%;
  transition: all 0.2s ease-in;

  &.hidden {
    opacity: 0;
  }
`;

const OptionsContainer = Styled.div`
  background-color: ${Theme.white};
  overflow-y: auto;
  max-height: 300px;

  &.nui-select-filter-container {
    background-color: ${Theme.blue};
    background: rgb(38 155 214);
    padding: 6px;
    margin-top: 2px;
    overflow: hidden;

    div {
      overflow: hidden;
    }
  }
`;

/**
 * @param {string} name
 */
const StyledNekoSelect = props => {
  const { id, name, value, scrolldown = false, isPro = false, onChange, className, disabled, isBusy = false,
    chevronIconSize = 24, textFiltering, ...rest } = props;

  const currentOption = React.Children.toArray(props.children)
    .find(child => child.props.value === value || child.props.checked)?.props;

  const label = currentOption?.label || 'Select';
  const description = currentOption?.description || props.description;
  const requirePro = currentOption?.requirePro || false;
  const icon = currentOption?.icon;
  const iconSize = currentOption?.iconSize;
  //const id = scrolldown ? props.id : currentOption?.id;

  const [ showOptions, toggleOptions ] = useState(false);
  const [ filterText, setFilterText ] = useState('');
  const componentRef = scrolldown ? useOutsideClick(() => { toggleOptions(false) }) : null;
  const showUpProOnly = requirePro && !isPro;

  const onOptionClick = (newValue) => {
    if (newValue !== value) {
      if (!onChange) {
        console.log(`The onChange handler it not set for this checkbox.`, props);
        return;
      }
      onChange(newValue, id);
    }
    if (scrolldown) {
      toggleOptions(false);
    }
  }

  const children = React.Children.map(props.children, child => {
    return React.cloneElement(child, {
      name: child.props.name || name,
      checked: child.props.value === value || child.props.checked,
      onClick: onOptionClick,
      scrolldown,
      isPro,
      disabled
    });
  });

  const filteredChildren = useMemo(() => {
    if (!filterText || !children.length) return children;

    const filtered = [];
    React.Children.forEach(children, child => {
      if (child.props.label.includes(filterText) || child.props.value.includes(filterText)) {
        filtered.push(child);
      }
    });
    return filtered;
  }, [children, filterText]);

  return scrolldown
    ? <Select id={id} ref={componentRef} {...rest}
        onClick={() => toggleOptions(!showOptions)}
        className={[ 'nui-select', className, showOptions ? 'show-options' : '']}
        childrenLength={children.length} data-is-disabled={disabled || isBusy}
      >
        <SelectedOption className={`nui-select-option ${isBusy ? 'isBusy' : ''}`}>
          {isBusy
            ? <>
              <NekoSpinner type="circle" size="20px" />
            </>
            : <>
              {icon && <NekoIcon icon={icon} width={iconSize} height={iconSize} style={{ marginRight: `${Math.max(iconSize - 15, 4)}px` }} />}
              <span className='nui-select-option-label'>{label}</span>
              <div className="rightContent">
                {showUpProOnly && <ProOnly />}
                {!disabled && <Icon icon={showOptions ? chevronUp : chevronDown} width={chevronIconSize} />}
              </div>
            </>}
        </SelectedOption>
        {description && <Description>{description}</Description>}
        {showOptions &&
          <Options className={`nui-option ${showOptions ? '' : 'hidden'}`}>
            {textFiltering && 
              <OptionsContainer className='nui-select-filter-container'>
                <NekoInput id="nui-select-filter" value={filterText} placeholder="Search..." onChange={(v) => setFilterText(v)}
                  onClick={(e) => e.stopPropagation()} style={{ background: Theme.white }} inputStyle={{ margin: 0, borderRadius: 0 }} autoFocus />
              </OptionsContainer>
            }
            <OptionsContainer>{[filteredChildren]}</OptionsContainer>
          </Options>
        }
      </Select>
    : children;
};

const RadioOption = Styled.div`
  font-family: ${Theme.fontFamily};
  margin-bottom: 6px;

  input {
    display: none;
  }

  label {
    cursor: pointer;
    display: flex;
  }

  .inner-container {
    margin-left: 4px;

    .label {
      display: block;
      font-size: 14px;
      line-height: 17px;
      padding-top: 4.5px;
      padding-bottom: 4px;
    }
    .description {
      display: block;
      font-size: ${Theme.fontSizeSmall};
    }
  }

  &.disabled {
    color: ${Theme.disabledForm};

    label {
      cursor: default;
    }
  }
`;

const SelectOption = Styled.div`
  background-color: ${Theme.blue};
  cursor: pointer;
  font-family: ${Theme.fontFamily};

  &:hover {
    filter: brightness(80%);
  }

  input {
    display: none;
  }

  .option {
    align-items: center;
    color: ${Theme.white};
    display: flex;
    justify-content: space-between;
    font-family: ${Theme.fontFamily};
    font-size: 14px;
    line-height: 17px;
    padding: 8px 12px;

    .option-group {
      align-items: center;
      display: flex;
    }
  }

  &.disabled {
    background-color: rgb(224 156 54);
    pointer-events: none;

    .option {
      color: rgb(255 255 255 / 35%);
    }
  }
`;

/**
 * @param {string} id
 * @param {string} name
 * @param {string} value
 * @param {bool} checked
 * @param {string} label
 * @param {string} description
 * @param {function} onClick
 */
const StyledNekoOption = props => {
  const { id, name, value, checked, icon, label, description, onClick, scrolldown, isPro = false,
    requirePro = false, disabled, iconSize = 20 } = props;

  const showUpProOnly = requirePro && !isPro;
  const disabledClass = showUpProOnly || disabled ? 'disabled' : '';

  const radioOption = (
    <RadioOption className={`nui-select ${props.className || ''} ${disabledClass}`}>
      <input id={id} name={name} type="radio" value={value} defaultChecked={checked} onClick={(e) => onClick(e.target.value)} disabled={showUpProOnly || disabled} />
      <label htmlFor={id}>
        <Icon icon={checked ? recordCircle : checkboxBlankCircleOutline} width="24px" color={showUpProOnly ? Theme.disabledForm : Theme.blue}/>
        <div className="inner-container">
          <span className="label">{label}<ProOnly className="inline" style={{ top: -1 }} show={showUpProOnly} /></span>
          {description ? <small className="description">{description}</small> : null}
        </div>
      </label>
    </RadioOption>
  );

  const selectOption = (
    <SelectOption className={`nui-select ${props.className || ''} ${disabledClass}`}>
      <input id={id} name={name} type="radio" value={value} defaultChecked={checked} disabled={showUpProOnly || disabled} />
      <div className="option" onClick={() => onClick(value)}>
        <div className="option-group">
          {icon && <NekoIcon icon={icon} width={iconSize} height={iconSize} style={{ marginRight: `${Math.max(iconSize - 11, 4)}px` }} />}
          {label}
        </div>
        <ProOnly show={showUpProOnly} />
      </div>
    </SelectOption>
  );

  return scrolldown ? selectOption : radioOption;
};

/**
 * The NekoSelect has 2 types, radio (default) or select.
 */
const NekoSelect = (props) => {
  return (<StyledNekoSelect {...props} />);
}

NekoSelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  onChange: PropTypes.func,
  isBusy: PropTypes.bool,
  chevronIconSize: PropTypes.number,
  textFiltering: PropTypes.bool,
};

NekoSelect.propTypes = {
  id: undefined,
  name: undefined,
  description: undefined,
  scrolldown: false,
  isPro: false,
  onChange: undefined,
  isBusy: false,
  chevronIconSize: 24,
  textFiltering: undefined,
};

const NekoOption = (props) => {
  return (<StyledNekoOption {...props} />);
}

NekoOption.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  requirePro: PropTypes.bool,
  icon: PropTypes.string,
  iconSize: PropTypes.number,
};

NekoOption.defaultProps = {
  id: undefined,
  name: undefined,
  value: undefined,
  checked: false,
  label: undefined,
  description: undefined,
  onClick: undefined,
  scrolldown: false,
  isPro: false,
  requirePro: false,
  icon: undefined,
  iconSize: 20,
};

export { NekoSelect, NekoOption };
